/**
 * Copyright 2025 Lumavec Group LLC
 *
 * TemplateCard Component
 * 
 * Displays a single architecture template with its features and description.
 */

import { Check, Globe, Monitor, Layers, Server, Smartphone, FileCode, Database, Bot, Cloud, Code } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import type { TemplateMetadata } from '../../lib/api';

interface TemplateCardProps {
  template: TemplateMetadata;
  isSelected: boolean;
  onSelect: () => void;
}

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  web: Globe,
  desktop: Monitor,
  platform: Layers,
  mobile: Smartphone,
  api: Server,
};

export function TemplateCard({ template, isSelected, onSelect }: TemplateCardProps) {
  const CategoryIcon = categoryIcons[template.category] || FileCode;
  
  return (
    <Card
      className={`relative p-6 cursor-pointer transition-all hover:border-primary/50 ${
        isSelected 
          ? 'border-2 border-primary bg-primary/5 ring-2 ring-primary/20' 
          : 'border hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-primary-foreground" />
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-lg ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
          <CategoryIcon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg mb-1 truncate">{template.displayName}</h3>
          <Badge variant="secondary" className="text-xs">
            {template.category}
          </Badge>
        </div>
      </div>
      
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {template.description}
      </p>
      
      {/* Features */}
      <div className="flex flex-wrap gap-2">
        {template.features.frontend && (
          <FeatureBadge icon={Code} label="Frontend" />
        )}
        {template.features.backend && (
          <FeatureBadge icon={Server} label="Backend" />
        )}
        {template.features.database && (
          <FeatureBadge icon={Database} label="Database" />
        )}
        {template.features.agents && (
          <FeatureBadge icon={Bot} label="AI Agents" />
        )}
        {template.features.deployable && (
          <FeatureBadge icon={Cloud} label="Deployable" />
        )}
      </div>
    </Card>
  );
}

function FeatureBadge({ 
  icon: Icon, 
  label 
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string;
}) {
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );
}

export default TemplateCard;

